import { RecommendationFeature } from '@wix/ambassador-premium-domains-v1-recommendation/types';
import { DomainsApiClient } from '@wix/domains-serverless-interface/react-kit';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRecommendations } from './useRecommendations';
import { DomainSearchGateModule } from '../module/domainSearchGate.module';
import { parseDomain } from '@wix/domains-serverless-interface';
const getFallbackInsight = t => tld => {
  return {
    headline: (() => {
      const key = `domain_search_gate.insight_widget.fallback_insight.${tld}.headline`;
      return t(key) !== key ? t(key) : t('domain_search_gate.insight_widget.fallback_insight.generic.headline');
    })(),
    bullets: Array.from({
      length: 3
    }, (_, index) => {
      const key = `domain_search_gate.insight_widget.fallback_insight.${tld}.insight_${index + 1}`;
      return t(key) !== key ? t(key) : t(`domain_search_gate.insight_widget.fallback_insight.generic.insight_${index + 1}`);
    })
  };
};
const DEFAULT_DEBOUNCE = 400;
export const useInsights = function (_temp) {
  var _onDemandInsight$insi, _onDemandInsight$insi2, _activeRecommendation, _getFallbackInsightFo, _activeRecommendation2, _getFallbackInsightFo2;
  let _ref = _temp === void 0 ? {
      debounce: DEFAULT_DEBOUNCE
    } : _temp,
    debounce = _ref.debounce,
    intent = _ref.intent,
    productType = _ref.productType,
    _ref$shouldFetch = _ref.shouldFetch,
    shouldFetch = _ref$shouldFetch === void 0 ? true : _ref$shouldFetch;
  const _useRecommendations = useRecommendations({
      productType,
      shouldFetchRecommendations: shouldFetch
    }),
    recommendations = _useRecommendations.recommendations,
    selectedFeatures = _useRecommendations.selectedFeatures;
  const _useState = useState(0),
    activeIndex = _useState[0],
    setActiveIndex = _useState[1];
  const _useState2 = useState(0),
    debouncedIndex = _useState2[0],
    setDebouncedIndex = _useState2[1];
  const hoverTimeoutRef = useRef(null);
  const _DomainSearchGateModu = DomainSearchGateModule.internals.useTranslations(),
    t = _DomainSearchGateModu.t;
  const getFallbackInsightForTLD = useCallback(tld => getFallbackInsight(t)(tld), [t]);
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);
  const activeRecommendation = useMemo(() => recommendations[activeIndex], [activeIndex, recommendations]);
  const debouncedRecommendation = useMemo(() => recommendations[debouncedIndex], [debouncedIndex, recommendations]);
  const isInsightOnDemand = useMemo(() => !!(debouncedRecommendation != null && debouncedRecommendation.domain) && !selectedFeatures.includes(RecommendationFeature.AI_INSIGHTS), [debouncedRecommendation == null ? void 0 : debouncedRecommendation.domain, selectedFeatures]);
  const _DomainsApiClient$hoo = DomainsApiClient.hooks.useGetInsight({
      domain: debouncedRecommendation == null ? void 0 : debouncedRecommendation.domain,
      intent
    }, shouldFetch && isInsightOnDemand && !!(debouncedRecommendation != null && debouncedRecommendation.domain), {
      staleTime: Infinity,
      cacheTime: Infinity
    }),
    onDemandInsight = _DomainsApiClient$hoo.data,
    isOnDemandInsightLoading = _DomainsApiClient$hoo.isLoading;
  const onHoverChange = useCallback(index => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setActiveIndex(index);
    hoverTimeoutRef.current = setTimeout(() => {
      setDebouncedIndex(index);
      hoverTimeoutRef.current = null;
    }, debounce);
  }, [debounce]);
  const parsedDomain = parseDomain((debouncedRecommendation == null ? void 0 : debouncedRecommendation.domain) ?? '');
  const onDemandDescription = onDemandInsight == null || (_onDemandInsight$insi = onDemandInsight.insight) == null ? void 0 : _onDemandInsight$insi.description;
  const onDemandBullets = onDemandInsight == null || (_onDemandInsight$insi2 = onDemandInsight.insight) == null ? void 0 : _onDemandInsight$insi2.bullets;
  const isOnDemandForActiveDomain = isInsightOnDemand && !!(debouncedRecommendation != null && debouncedRecommendation.domain) && debouncedRecommendation.domain === (activeRecommendation == null ? void 0 : activeRecommendation.domain);
  const description = (isOnDemandForActiveDomain ? onDemandDescription : activeRecommendation == null || (_activeRecommendation = activeRecommendation.insight) == null ? void 0 : _activeRecommendation.description) ?? ((_getFallbackInsightFo = getFallbackInsightForTLD(parsedDomain.tld)) == null ? void 0 : _getFallbackInsightFo.headline);
  const bullets = (isOnDemandForActiveDomain ? onDemandBullets : activeRecommendation == null || (_activeRecommendation2 = activeRecommendation.insight) == null ? void 0 : _activeRecommendation2.bullets) ?? ((_getFallbackInsightFo2 = getFallbackInsightForTLD(parsedDomain.tld)) == null ? void 0 : _getFallbackInsightFo2.bullets);
  const isDebouncing = recommendations.length > 0 && activeIndex !== debouncedIndex;
  const isLoading = isDebouncing || isOnDemandInsightLoading;
  const setIndex = useCallback(domain => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    const index = recommendations.findIndex(r => r.domain === domain);
    if (index !== -1) {
      setActiveIndex(index);
      setDebouncedIndex(index);
    }
  }, [recommendations]);
  return {
    domain: activeRecommendation == null ? void 0 : activeRecommendation.domain,
    description,
    bullets,
    onHoverChange,
    isLoading,
    setIndex
  };
};
//# sourceMappingURL=useInsights.js.map